import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Chip,
  Alert,
  Box,
  Divider,
  Link,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Schedule,
  Refresh,
  Email,
  Message,
  OpenInNew,
} from '@mui/icons-material';
import { apiClient } from '@/utils/api-client';
interface BookingAutomationStatusProps {
  booking: {
    bookingId: string;
    listingId: string;
    checkInFormUrl?: string;
    checkInFormExpiration?: string;
    communicationLog?: Array<{
      recordId: string;
      type: 'email' | 'whatsapp' | 'sms';
      recipient: string;
      status: 'sent' | 'delivered' | 'failed';
      sentAt: string;
    }>;
    status: string;
    createdAt: string;
  };
  onRetrySuccess?: () => void;
}

export const BookingAutomationStatus: React.FC<BookingAutomationStatusProps> = ({
  booking,
  onRetrySuccess
}) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryMessage, setRetryMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Check automation status
  const hasCheckInForm = Boolean(booking.checkInFormUrl);
  const isCheckInFormExpired = booking.checkInFormExpiration 
    ? new Date(booking.checkInFormExpiration) < new Date() 
    : false;
  
  const communicationsSent = booking.communicationLog?.length || 0;
  const failedCommunications = booking.communicationLog?.filter(c => c.status === 'failed').length || 0;
  const successfulCommunications = booking.communicationLog?.filter(c => c.status === 'sent' || c.status === 'delivered').length || 0;
  
  const checkInFormComplete = hasCheckInForm && !isCheckInFormExpired;
  const communicationsComplete = successfulCommunications > 0 && failedCommunications === 0;
  const automationComplete = checkInFormComplete && communicationsComplete;
  
  const checkInFormNeedsAction = !hasCheckInForm || isCheckInFormExpired;
  const communicationsNeedAction = communicationsSent === 0 || failedCommunications > 0;

  const retryAutomation = async (tasks: ('checkInForm' | 'confirmations')[]) => {
    setIsRetrying(true);
    
    try {
      const response = await apiClient.post(`/bookings/${booking.bookingId}/retry-automation?listingId=${booking.listingId}`, {
        retryTasks: tasks
      });

      const result = response.data;
      
      setRetryMessage({
        type: 'success',
        text: `Successfully retried: ${tasks.join(', ')}`
      });

      onRetrySuccess?.();
      
      // Clear message after 5 seconds
      setTimeout(() => setRetryMessage(null), 5000);
      
    } catch (error) {
      console.error('Error retrying automation:', error);
      setRetryMessage({
        type: 'error',
        text: 'Failed to retry automation tasks. Please try again.'
      });
      
      // Clear message after 5 seconds
      setTimeout(() => setRetryMessage(null), 5000);
    } finally {
      setIsRetrying(false);
    }
  };

  const getStatusIcon = (hasFeature: boolean, hasFailed: boolean = false) => {
    if (hasFailed) return <Cancel sx={{ color: 'error.main', fontSize: 16 }} />;
    if (hasFeature) return <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />;
    return <Schedule sx={{ color: 'warning.main', fontSize: 16 }} />;
  };

  const getStatusBadge = () => {
    if (automationComplete) {
      return <Chip label="Complete" color="success" size="small" />;
    }
    if (checkInFormNeedsAction || communicationsNeedAction) {
      return <Chip label="Action Required" color="warning" size="small" />;
    }
    return <Chip label="In Progress" color="default" size="small" />;
  };

  return (
    <Card sx={{ width: '100%' }}>
      <CardHeader>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="h2">
            Booking Automation
          </Typography>
          {getStatusBadge()}
        </Box>
      </CardHeader>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Retry Message */}
        {retryMessage && (
          <Alert severity={retryMessage.type} onClose={() => setRetryMessage(null)}>
            {retryMessage.text}
          </Alert>
        )}

        {/* Check-in Form Status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon(hasCheckInForm && !isCheckInFormExpired, isCheckInFormExpired)}
            <Typography variant="body2">Check-in Form</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasCheckInForm ? (
              <>
                <Typography variant="caption" color="text.secondary">
                  {isCheckInFormExpired ? (
                    <span style={{ color: 'error.main' }}>Expired</span>
                  ) : (
                    <span>Active</span>
                  )}
                </Typography>
                {!isCheckInFormExpired && (
                  <Link
                    href={booking.checkInFormUrl?.startsWith('http') ? booking.checkInFormUrl : `${window.location.origin}${booking.checkInFormUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}
                  >
                    <Typography variant="caption">View Form</Typography>
                    <OpenInNew sx={{ fontSize: 12 }} />
                  </Link>
                )}
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">Not Generated</Typography>
            )}
          </Box>
        </Box>

        {/* Communications Status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon(communicationsSent > 0, failedCommunications > 0)}
            <Typography variant="body2">Confirmations Sent</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              {communicationsSent > 0 ? (
                <>
                  {communicationsSent} sent
                  {failedCommunications > 0 && (
                    <span style={{ color: 'error.main', marginLeft: 4 }}>({failedCommunications} failed)</span>
                  )}
                </>
              ) : (
                'None sent'
              )}
            </Typography>
          </Box>
        </Box>

        {/* Communication Details */}
        {booking.communicationLog && booking.communicationLog.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" fontWeight="medium" color="text.secondary">
              Communication History
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {booking.communicationLog.map((comm) => (
                <Box key={comm.recordId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {comm.type === 'email' ? (
                      <Email sx={{ fontSize: 12 }} />
                    ) : (
                      <Message sx={{ fontSize: 12 }} />
                    )}
                    <Typography variant="caption">{comm.type}</Typography>
                    <Typography variant="caption" color="text.secondary">to {comm.recipient}</Typography>
                  </Box>
                  <Chip 
                    label={comm.status}
                    color={comm.status === 'sent' || comm.status === 'delivered' ? 'success' : 'error'}
                    size="small"
                    sx={{ fontSize: '0.6rem', height: 16 }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Individual Automation Actions */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" fontWeight="medium" color="text.secondary">
            Automation Actions
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {/* Check-in Form Actions */}
            {checkInFormNeedsAction && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => retryAutomation(['checkInForm'])}
                disabled={isRetrying}
                startIcon={<Refresh sx={{ fontSize: 12 }} />}
              >
                {!hasCheckInForm ? 'Generate Check-in Form' : 'Regenerate Check-in Form'}
              </Button>
            )}
            
            {/* Communication Actions */}
            {communicationsNeedAction && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => retryAutomation(['confirmations'])}
                disabled={isRetrying}
                startIcon={<Refresh sx={{ fontSize: 12 }} />}
              >
                {communicationsSent === 0 ? 'Send Confirmations' : 'Retry Failed Confirmations'}
              </Button>
            )}
            
            {/* Show message if no actions needed */}
            {!checkInFormNeedsAction && !communicationsNeedAction && (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                All automation tasks are complete
              </Typography>
            )}
          </Box>
        </Box>

        {/* Success Message */}
        {automationComplete && (
          <Alert severity="success" sx={{ mt: 1 }}>
            All automation tasks completed successfully. Check-in form is active and confirmations have been sent.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};